import React, { useState } from 'react';
import styled from 'styled-components';
import useImageQuery from '@/hooks/common/useImageQuery';
import { Document, Page, pdfjs } from 'react-pdf';
import TextComponent from '../TextComponent';
import { ReactComponent as Print } from '@/assets/images/common/print.svg';
import { ReactComponent as Download } from '@/assets/images/common/download.svg';
import { ReactComponent as Hospital } from '@/assets/images/common/hospital.svg';
import { DoctorInfo } from '@/apis/order/interface';
import useModal from '@/hooks/common/useModal';
import EmergencyMessageForm from '@/pages/OrderDetailPage/containers/EmergencyMessageForm';
import theme from '@/styles/theme';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import ArrowButtonLeftMin from '../icon/ArrowButtonLeftMin';
import ArrowButtonRightMin from '../icon/ArrowButtonRightMin';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface Props {
  orderId: number;
  id: string;
  uri: string;
  fileType: string;
  scale: number;
  imageZoom?: (on: boolean) => void;
  originalName: string;
  doctorInfo?: DoctorInfo;
}

const imageWithAuthView = (props: Props) => {
  const {
    orderId,
    id,
    uri,
    fileType,
    scale,
    imageZoom,
    originalName,
    doctorInfo
  } = props;
  const {
    value: { imageUri: image, buffer }
  } = useImageQuery(uri);
  const [EmergencyMessageFormModal, setEmergencyMessageFormModal] =
    useModal(false);
  const [currentPdfPage, setCurrentPdfPage] = useState(1);
  const [pdfTotalPage, setPdfTotalPage] = useState(1);

  const openEmergencyMessageForm = () => {
    setEmergencyMessageFormModal(true);
  };
  const handlePrint = async () => {
    const filetype = fileType === 'pdf' ? 'application/pdf' : 'image/jpg';
    const blob = new Blob([buffer!], { type: filetype });
    const fileUrl = URL.createObjectURL(blob);
    const style = document.createElement('style');
    style.appendChild(
      document.createTextNode(`  
    @media print {
      html, body, img {
        width: 210mm !important;
        height: 297mm !important;
        -webkit-print-color-adjust: exact;
      }
    }
    @page {
      size: 210mm 297mm;
    }
      `)
    );
    const frame = document.createElement('iframe');
    frame.src = fileUrl;

    frame.onload = function () {
      if (frame.contentWindow) {
        frame.style.height = '0';
        frame.style.visibility = 'hidden';
        frame.contentDocument?.body.appendChild(style);
        frame.contentWindow.print();
      }
    };
    document.body.appendChild(frame);
  };

  const onClickPaginationButton = (type: 'prev' | 'next') => {
    if (type === 'prev') {
      if (currentPdfPage > 1) {
        return setCurrentPdfPage(currentPdfPage - 1);
      }
    }
    if (type === 'next') {
      if (currentPdfPage !== pdfTotalPage) {
        return setCurrentPdfPage(currentPdfPage + 1);
      }
    }
  };
  function download(dataurl: any) {
    const link = document.createElement('a');
    link.href = dataurl;
    link.download = originalName;
    link.click();
  }

  const renderPdfError = () => (
    <Center>
      <TextComponent>
        일시적으로 처방전을 불러오는데 실패하였습니다.
      </TextComponent>
    </Center>
  );

  const renderPdfLoading = () => (
    <Center>
      <TextComponent>처방전을 불러오는 중입니다.</TextComponent>
    </Center>
  );

  return (
    <ImageWrapper>
      <Image zoom={false}>
        {fileType !== 'pdf' ? (
          <img
            src={image}
            style={{
              width: `${420 * scale}px`,
              height: 'auto'
            }}
          />
        ) : (
          <>
            <Document
              file={image}
              error={renderPdfError}
              options={{
                cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
                cMapPacked: true,
                standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts`
              }}
              loading={renderPdfLoading}
              onLoadSuccess={({ numPages }) => {
                setPdfTotalPage(numPages);
              }}>
              <Page scale={scale} pageNumber={currentPdfPage} width={420} />
            </Document>
            {pdfTotalPage > 1 && (
              <PdfPaginationWrapper>
                <PdfPagination>
                  <ArrowButton onClick={() => onClickPaginationButton('prev')}>
                    <ArrowButtonLeftMin />
                  </ArrowButton>
                  <span>{currentPdfPage}/{pdfTotalPage}</span>
                  <ArrowButton onClick={() => onClickPaginationButton('next')}>
                    <ArrowButtonRightMin />
                  </ArrowButton>
                </PdfPagination>
              </PdfPaginationWrapper>
            )}
          </>
        )}
      </Image>
      <ButtonWrapper className="no-print">
        <Button onClick={() => download(image)}>
          <Download />
          <TextComponent>저장</TextComponent>
        </Button>
        <Button onClick={handlePrint}>
          <Print />
          <TextComponent>프린트</TextComponent>
        </Button>
        {doctorInfo && (
          <Button
            style={{ borderColor: theme.DF_COLOR.RED700 }}
            onClick={openEmergencyMessageForm}>
            <Hospital />
            <TextComponent color={theme.DF_COLOR.RED700}>긴급 연락</TextComponent>
          </Button>
        )}
      </ButtonWrapper>
      <EmergencyMessageFormModal thin clickOverlayToClose={false}>
        {doctorInfo && (
          <EmergencyMessageForm
            orderId={orderId}
            onClickClose={() => setEmergencyMessageFormModal(false)}
          />
        )}
      </EmergencyMessageFormModal>
    </ImageWrapper>
  );
};

export default imageWithAuthView;

const ImageWrapper = styled.div`
  overflow: hidden;

  .print {
    display: none;
  }
`;

const Image = styled.div<{ zoom: boolean }>`
  ${({ zoom }) => zoom && 'cursor: zoom-in'};
  min-height: 38rem;
  border: 1px solid ${theme.DF_COLOR.GRAY100};
  position: relative;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  > * {
    &:not(:last-child) {
      margin-right: 15px;
    }
  }
`;

const Button = styled.div`
  border: 1px solid #ababab;
  border-radius: 5px;
  padding: 12px 0;
  width: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px 0 20px 0;
  cursor: pointer;

  > * {
    &:nth-child(1) {
      margin-right: 8px;
    }
  }
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 50%;
`;

const pageStyle = `
  @page {
    size: A4;
    margin: 4mm;
  }

  @media print {
    html, body {
      width: 210mm !important;
      height: 297mm !important;
      -webkit-print-color-adjust: exact;
    }
    .no-print * {
      display: none !important;
    }
    .print {
      display: block;
    }

  }
  @media print {
    .pagebreak {
      page-break-before: always;
    }
    
  }
`;

const PdfPaginationWrapper = styled.div`
  position: absolute;
  bottom: 30px;
  width: 100%;
  text-align: center;
`;

const PdfPagination = styled.div`
  display: inline-flex;
  padding: 8px 12px;
  border-radius: 80px;
  align-items: center;

  span {
    width: 60px;
    text-align: center;
  }
  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI16SB};
    color: ${theme.DF_COLOR.WHITE};
    background: ${theme.DF_COLOR.DIMMED80};
  `};
`;

const ArrowButton = styled.button`
  display: flex;
  padding: 0;
  cursor: pointer;
`;
